import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@apollo/client";
import { Button, Spin, Table } from "antd";
import styled from "styled-components";
import { isNumber, parseNumber } from "utils/fp";

import useColumns from "hooks/useColumns";
import { useEvaUnitCore } from "hooks/useEvaUnitCore";
import { useTypeWellUnitConversion } from "hooks/useTypeWellUnitConversion";
import useUserUnit from "hooks/useUserUnit";

import { GET_FORECAST_DETAILS, GET_WELL_LIST_RECORDS } from "api/userArps";

import {
  convertNominalToDeclineType,
  declineTypes
} from "components/arps/utils/arpsUtils";

import { FIELD_NAMES, ForecastActivityType, TYPE_WELLS } from "../../../constants";
import { UserArpsItem, WellListRecordCollection } from "../../../models/UserArpsModel";
import { convertForecastConstantUnits } from "../../../utils/arps/convertForecastConstantUnits";
import { toExpectedDisplayName } from "../../../utils/arps/productMapping";
import { SubHeading } from "../../base";
import { IconSpinner } from "../../icons";
import { ForecastChangeRecords } from "./ForecastChangeRecords";
import { ForecastWellListRecords } from "./ForecastWellListRecords";

export let arpsWasm: typeof import("wasm/arps") = undefined;
import("wasm/arps.js").then((x) => {
  arpsWasm = x;
});

export interface ForecastDetailsModel {
  arpsItem: UserArpsItem;
  declineType: string;
  getTitle: (item, type) => string;
  type: ForecastActivityType;
  hasTypeWellSaved: boolean;
}

// TODO: move out
const getChangeRecordsButtonTitlePrefix = (mode) => {
  if (mode == "ChangeRecords") {
    return "Hide";
  } else if (mode === "WellListHistory") {
    return "View";
  } else {
    return "View";
  }
};

const getWellListHistoryButtonTitlePrefix = (mode) => {
  if (mode == "WellListHistory") {
    return "Hide";
  } else if (mode === "ChangeRecords") {
    return "View";
  } else {
    return "View";
  }
};

export default function ForecastDetails({
  arpsItem,
  declineType,
  getTitle,
  type,
  hasTypeWellSaved
}: ForecastDetailsModel) {
  const [mode, setMode] = useState<"Details" | "ChangeRecords" | "WellListHistory">(
    "Details"
  );

  const {
    convertLengthToDisplay,
    convertProppantIntensityToDisplay,
    lengthUnit,
    proppantIntensityUnit
  } = useTypeWellUnitConversion();

  const unitType = useUserUnit();
  const { isReady, convertUnitToUnit, getTargetUnit } = useEvaUnitCore();
  const groupByColumns = useColumns();

  // Check if we need to lazy load the forecast details
  // If arpsItem doesn't have arps data, we need to fetch it
  const needsLazyLoad = !arpsItem.arps || arpsItem.arps.length === 0;

  const { data: forecastDetailsData, loading: isForecastDetailsLoading } = useQuery(
    GET_FORECAST_DETAILS,
    {
      variables: {
        forecastId: arpsItem.id,
        folderId: arpsItem.folderId
      },
      skip: !needsLazyLoad // Skip if we already have the data
    }
  );

  // Use the lazy-loaded data if available, otherwise use the passed arpsItem
  const forecastData = useMemo(() => {
    if (needsLazyLoad && forecastDetailsData?.forecastDetails) {
      return {
        ...arpsItem,
        arps: forecastDetailsData.forecastDetails.arps ?? [],
        constants: forecastDetailsData.forecastDetails.constants ?? [],
        wellData: forecastDetailsData.forecastDetails.wellData
      };
    }
    return arpsItem;
  }, [arpsItem, needsLazyLoad, forecastDetailsData]);

  const {
    data: wellListRecordData,
    loading: isWellListRecordLoading,
    refetch: refetchWellListRecords
  } = useQuery<WellListRecordCollection>(GET_WELL_LIST_RECORDS, {
    variables: {
      forecastId: arpsItem.id
    }
  });

  useEffect(() => {
    if (hasTypeWellSaved) {
      refetchWellListRecords();
    }
  }, [hasTypeWellSaved]);

  function addKeyToData(data) {
    return data.map((x, i) => {
      const copy = { ...x, key: i + 1, product: toExpectedDisplayName(x.product) };
      // If the mode is ChangeRecords, convert the units of the constants otherwise it displays the raw db values
      if (mode === "ChangeRecords") {
        return convertForecastConstantUnits([copy])[0];
      }
      // If the mode is not ChangeRecords, return the copy as is as it is using the front end values
      return copy;
    });
  }

  function addWellDataToSource(data) {
    const source = [];
    const copy = { ...data };
    copy.key = 1;

    if (copy.HzLength !== undefined && copy.HzLength !== "-") {
      copy.HzLength = convertLengthToDisplay(copy.HzLength);
    }
    if (copy.StageSpacing !== undefined && copy.StageSpacing !== "-") {
      copy.StageSpacing = convertLengthToDisplay(copy.StageSpacing);
    }
    if (copy.ProppantIntensity !== undefined && copy.ProppantIntensity !== "-") {
      copy.ProppantIntensity = convertProppantIntensityToDisplay(copy.ProppantIntensity);
    }

    if (copy.AdditionalData && Array.isArray(copy.AdditionalData)) {
      copy.AdditionalData = copy.AdditionalData.map((item) => {
        const property = item.field?.replace(/\.Forecast$/, "");
        const column = groupByColumns.find((c) => c.property === property);
        const sourceUnit = column?.units?.[0];

        if (sourceUnit && isReady && item.value !== undefined) {
          const targetUnit = getTargetUnit(sourceUnit, unitType);
          if (sourceUnit !== targetUnit) {
            return {
              ...item,
              value: convertUnitToUnit(sourceUnit, targetUnit, item.value, 2)
            };
          }
        }
        return item;
      });
    }

    source.push(copy);
    return source;
  }

  const calculateCumVolume = (arps) => {
    let cumulativeVolume = 0;
    return arps.map((item, index) => {
      // Calculations for the Cum Volume column, will reset upon product change
      if (index != 0 && item.product !== arps[index - 1].product) {
        cumulativeVolume = 0;
      }

      if (item.endDate && item.startDate && arpsWasm) {
        const segmentVolume = arpsWasm.getSegmentVolume(
          item.qi,
          item.di,
          item.b,
          item.qf,
          BigInt(Math.round(new Date(item.startDate).getTime() / 1000)),
          BigInt(Math.round(new Date(item.endDate).getTime() / 1000))
        );
        cumulativeVolume += segmentVolume * 1e-3;
      } else {
        cumulativeVolume += (item.trendCum || 0) * 1e-3;
      }

      //The final Cum Volume value of a product should be bolded
      const isBold =
        (index < arps.length - 1 && item.product !== arps[index + 1].product) ||
        index == arps.length - 1;

      return {
        ...item,
        di: convertNominalToDeclineType({
          nominalDeclineValueDecimal: item.di,
          bValue: item.b,
          type: declineType
        }),
        df: convertNominalToDeclineType({
          nominalDeclineValueDecimal: item.df,
          bValue: item.b,
          type: declineType
        }),
        cumulativeVolume:
          isNaN(cumulativeVolume) || !isFinite(cumulativeVolume) ? "-" : cumulativeVolume,
        bold: isBold
      };
    });
  };

  function getArps(arps) {
    return calculateCumVolume(arps);
  }

  function getDeclineWithType(title) {
    return `${title} (${declineTypes[declineType]})`;
  }

  const columns = [
    {
      title: "Product",
      dataIndex: "product"
    },
    {
      title: "Segment",
      dataIndex: "segmentIndex"
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      render: formatDate
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: formatDate
    },
    {
      title: "Qi",
      dataIndex: "qi",
      render: roundToOneDecimalPlace
    },
    {
      title: getDeclineWithType("Di"),
      dataIndex: "di",
      render: roundToOneDecimalPlace
    },
    {
      title: "B",
      dataIndex: "b",
      render: roundToOneDecimalPlace
    },
    {
      title: "Qf",
      dataIndex: "qf",
      render: roundToOneDecimalPlace
    },
    {
      title: "Trend Cum",
      dataIndex: "trendCum",
      render: (value) => {
        return <span>{roundToOneDecimalPlace(value * 1e-3)}</span>;
      }
    },
    {
      title: getDeclineWithType("Df"),
      dataIndex: "df",
      render: roundToOneDecimalPlace
    },
    {
      title: "Max Rate",
      dataIndex: "maxRate",
      render: roundToOneDecimalPlace
    },
    {
      title: "Cum Volume",
      dataIndex: "cumulativeVolume",
      render: (value, record) => {
        const fontWeight = record.bold ? "bold" : "normal";
        return (
          <span style={{ fontWeight }}>
            {typeof value == "number" ? roundToOneDecimalPlace(value) : value}
          </span>
        );
      }
    }
  ];

  const wellDataColumns = useMemo(
    () => [
      {
        title: "Stages Actual",
        dataIndex: "Stage"
      },
      {
        title: `Stages Spacing (${lengthUnit})`,
        dataIndex: "StageSpacing",
        render: roundToOneDecimalPlace
      },
      {
        title: "Proppant Total",
        dataIndex: "Proppant",
        render: (value) => (isNaN(value) ? "" : roundToOneDecimalPlace(value))
      },
      {
        title: `Proppant Intensity (${proppantIntensityUnit})`,
        dataIndex: "ProppantIntensity",
        render: roundToOneDecimalPlace
      },
      {
        title: `${FIELD_NAMES.HZ_LENGTH} (${lengthUnit})`,
        dataIndex: "HzLength",
        render: roundToOneDecimalPlace
      }
    ],
    [lengthUnit, proppantIntensityUnit]
  );

  const RenderDetails = () => (
    <>
      <SubHeading>Arps Segments</SubHeading>
      <TableWrapper className="arps-table">
        <Table
          pagination={false}
          dataSource={addKeyToData(getArps(forecastData.arps ?? []))}
          columns={columns}
        />
      </TableWrapper>

      {forecastData.constants && (
        <>
          <SubHeading>Constants</SubHeading>
          <TableWrapper className="arps-table">
            <Table
              pagination={false}
              dataSource={addKeyToData(forecastData.constants ?? [])}
              columns={constantColumns}
            />
          </TableWrapper>
        </>
      )}

      {type === TYPE_WELLS ? (
        <div style={{ paddingTop: "18px" }}>
          <SubHeading>Well Data</SubHeading>
          <div className="arps-table">
            <Table
              pagination={false}
              dataSource={addWellDataToSource(
                forecastData.wellData ?? {
                  Stage: "-",
                  StageSpacing: "-",
                  Proppant: "-",
                  ProppantIntensity: "-",
                  HzLength: "-"
                }
              )}
              columns={wellDataColumns}
            />
          </div>
        </div>
      ) : null}
    </>
  );

  const changeRecordsButtonTitlePrefix = useMemo(
    () => getChangeRecordsButtonTitlePrefix(mode),
    [mode]
  );
  const wellListHistoryButtonTitlePrefix = useMemo(
    () => getWellListHistoryButtonTitlePrefix(mode),
    [mode]
  );

  const RenderChangeRecords = () => {
    return (
      <OverlayWrapper>
        <ForecastChangeRecords forecastId={arpsItem.id} />
      </OverlayWrapper>
    );
  };

  // Show loading spinner when lazy loading forecast details
  if (needsLazyLoad && isForecastDetailsLoading) {
    return (
      <LoadingContainer>
        <Spin indicator={<IconSpinner />} />
      </LoadingContainer>
    );
  }

  if (forecastData && forecastData.arps && forecastData.arps.length > 0) {
    return (
      <>
        <HeadingWrapper>
          <HeaderLeftItems>
            <SubHeading element="h1">
              {getTitle(forecastData, type)}{" "}
              {forecastData?.source ? ` (${forecastData.source})` : ""}
            </SubHeading>
          </HeaderLeftItems>

          {type === TYPE_WELLS && (
            <HeaderRightItems>
              <Button
                onClick={(e) => {
                  setMode(() => {
                    if (mode == "WellListHistory") {
                      return "Details";
                    } else if (mode === "ChangeRecords") {
                      return "WellListHistory";
                    } else {
                      return "WellListHistory";
                    }
                  });
                  e.stopPropagation();
                }}
                type="default"
                title={
                  "View Well Lists"
                }>{`${wellListHistoryButtonTitlePrefix} Well Lists`}</Button>
              <Button
                onClick={(e) => {
                  setMode(() => {
                    if (mode == "ChangeRecords") {
                      return "Details";
                    } else if (mode === "WellListHistory") {
                      return "ChangeRecords";
                    } else {
                      return "ChangeRecords";
                    }
                  });
                  e.stopPropagation();
                }}
                type="default"
                title={
                  "View Change Records"
                }>{`${changeRecordsButtonTitlePrefix} Change Records`}</Button>
            </HeaderRightItems>
          )}
        </HeadingWrapper>
        <RenderDetails />
        {/* change records is positioned over the details
        to retain the width and height of the popover*/}
        {mode == "ChangeRecords" && <RenderChangeRecords />}
        {mode == "WellListHistory" && (
          <OverlayWrapper>
            <ForecastWellListRecords
              data={wellListRecordData}
              isLoading={isWellListRecordLoading}
              forecastId={arpsItem.id}
            />
          </OverlayWrapper>
        )}
      </>
    );
  }
  return null;
}

function roundToOneDecimalPlace(value) {
  if (value == null || value === "") return undefined;
  const num = parseNumber(value, NaN);
  return isNumber(num) ? num.toFixed(1) : undefined;
}

function formatDate(date) {
  const dateAndTime = date.split("T");
  return dateAndTime[0];
}

const constantColumns = [
  {
    title: "Ratio",
    dataIndex: "product",
    render: (productName, productRecord) =>
      productRecord?.unit ? `${productName} (${productRecord.unit})` : productName
  },
  {
    title: "Constant",
    dataIndex: "value"
  }
];

const TableWrapper = styled.div`
  max-height: 500px;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const HeadingWrapper = styled.div`
  margin-bottom: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeftItems = styled.div`
  margin-right: auto;
`;

const HeaderRightItems = styled.div`
  display: flex;
  gap: 10px;
`;

const OverlayWrapper = styled.div`
  position: absolute;
  top: 60px;
  right: 0;
  left: 0;
  bottom: 0;
  padding: 0px 20px 20px 20px;
  background: white;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  min-width: 300px;
`;
